import { z } from 'zod';

import { TournamentFormatSchema } from '../enums';
import { IdSchema } from './common';
import { EmblemSchema } from './identity';

/**
 * Турнир шестого сбора (BLUEPRINT Stage 12, задача 3): подбор соперника ops в админке
 * (`admin.tournaments.*`) и автопредложение по городу, активности и полосе уровня ±1.
 */

export const TournamentCandidateSchema = z.object({
  squadId: IdSchema,
  name: z.string().nullable(),
  emblem: EmblemSchema.nullable(),
  levelBand: z.number().int().min(1).max(5),
  capacity: z.number().int().positive(),
  weekday: z.number().int().min(1).max(7),
  startTime: z.string(),
  /** Сбор-турнир соперника, к которому привяжется наш */
  gatheringId: IdSchema,
  gatheringStartsAt: z.string().datetime(),
  score: z.number(),
});
export type TournamentCandidate = z.infer<typeof TournamentCandidateSchema>;

export const TournamentFormatLockSchema = z.enum(['gathering_closed', 'has_bookings']);
export type TournamentFormatLock = z.infer<typeof TournamentFormatLockSchema>;

export const TournamentRowSchema = z.object({
  gatheringId: IdSchema,
  squadId: IdSchema,
  squadName: z.string().nullable(),
  /** Основная активность отряда, вечер и время — подпись отряда без названия */
  squadActivityName: z.string(),
  weekday: z.number().int().min(1).max(7),
  startTime: z.string(),
  cityName: z.string(),
  /** Активность самого сбора (слот `tournament`) */
  activityName: z.string(),
  levelBand: z.number().int().min(1).max(5),
  startsAt: z.string().datetime(),
  timezone: z.string(),
  opponentSquadId: IdSchema.nullable(),
  opponentName: z.string().nullable(),
  /** Лучший кандидат от автопредложения (пусто — соперника нет) */
  suggestion: TournamentCandidateSchema.nullable(),
  result: z.object({ ours: z.number().int(), theirs: z.number().int() }).nullable(),
  /** Заданный ops формат (Stage 17, задача 5); null — не задан, читается как `STANDARD` */
  tournamentFormat: TournamentFormatSchema.nullable(),
  /**
   * Почему формат сейчас не меняется (та же проверка, что в `setFormat`): сбор пары начался или
   * закрыт; на паре есть живая запись (бронь или оплаченное место). null — можно менять.
   */
  formatLock: TournamentFormatLockSchema.nullable(),
  /** Сбор создал ops в админке (`opsCreated`), а не конструктор сезона */
  opsCreated: z.boolean(),
});
export type TournamentRow = z.infer<typeof TournamentRowSchema>;

export const AdminTournamentsListInputSchema = z
  .object({ cityId: IdSchema.optional(), withOpponent: z.boolean().optional() })
  .default({});
export type AdminTournamentsListInput = z.infer<typeof AdminTournamentsListInputSchema>;

export const AdminTournamentAssignInputSchema = z.object({
  gatheringId: IdSchema,
  opponentGatheringId: IdSchema,
});
export type AdminTournamentAssignInput = z.infer<typeof AdminTournamentAssignInputSchema>;

export const AdminTournamentClearInputSchema = z.object({ gatheringId: IdSchema });

/** `admin.tournaments.setFormat` (Stage 17, задача 5): формат у сбора и близнеца сразу. */
export const AdminTournamentSetFormatInputSchema = z.object({
  gatheringId: IdSchema,
  format: TournamentFormatSchema,
});
export type AdminTournamentSetFormatInput = z.infer<typeof AdminTournamentSetFormatInputSchema>;
